import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import posthog from 'posthog-js';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** Tags every event so portal telemetry can be told apart from other clients in the same PostHog project. */
export const TELEMETRY_APP_NAME = 'client-web-portal';

@Injectable({
  providedIn: 'root',
})
export class PostHogService {
  private isInitialized = false;

  constructor(private readonly router: Router) {}

  /**
   * Initializes PostHog SDK through the NestJS Telemetry Domain Proxy.
   * Enforces:
   * 1. 0 Outbound requests to *.posthog.com (100% routed through /patient/telemetry).
   * 2. disable_session_recording: true (Strictly NO Screen Capture / DOM Replays).
   * 3. Autocapture of clicks, session dwell times, pageviews, and performance diagnostics.
   */
  public init(apiKey?: string, customApiHost?: string): void {
    if (this.isInitialized || !environment.telemetryEnabled) return;
    this.isInitialized = true;

    // Use domain proxy: configured endpoint constant
    let defaultTunnelHost = environment.endPoints?.telemetryTunnel || '';
    if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && defaultTunnelHost.startsWith('http://')) {
      defaultTunnelHost = defaultTunnelHost.replace(/^http:\/\//, 'https://');
    }

    const tunnelHost = customApiHost || defaultTunnelHost;
    const projectKey = apiKey || environment.posthogApiKey || 'ioh-client-telemetry';

    try {
      posthog.init(projectKey, {
        api_host: tunnelHost,
        ui_host: environment.posthogUiHost || 'https://us.posthog.com',

        // 1. HIPAA / SECURITY GUARDRAILS
        disable_session_recording: true, // STRICT: ZERO Screen Recording / Replays
        capture_heatmaps: false,         // Disable heatmap DOM recordings
        advanced_disable_flags: true,    // Prevent autocapture from blocking on /array/<token>/config
        disable_compression: true,       // Send standard JSON (required for in-memory proxy parsing)
        mask_all_text: true,             // PHI guardrail: never send element text
        mask_all_element_attributes: true, // PHI guardrail: never send element attributes (href, title, aria-label, value)
        capture_exceptions: false,       // Only captureError() reports exceptions; autocapture would send raw messages

        // 2. USER BEHAVIOR & INTERACTION TRACKING
        autocapture: true, // Captures element clicks, button taps, and tab selections
        capture_pageview: false, // Managed by Angular SPA router listener below to avoid duplicate landing counts
        capture_pageleave: true, // Tracks exact time spent on each page before exiting
        capture_performance: true, // Core Web Vitals, page load latencies

        // 3. CLIENT-SIDE PRE-SANITIZATION GUARDRAIL
        before_send: (event: any) => {
          if (!event) return event;
          // Person properties ($set_once: $initial_current_url, $session_entry_url, ...) carry the raw
          // location.href too, so they are scrubbed the same way as the event's own properties.
          [event.properties, event.$set, event.$set_once].forEach((props) => this.sanitizeUrlProperties(props));

          if (event.properties?.['$el_text']) {
            event.properties['$el_text'] = '';
          }

          return event;
        },
        loaded: (phInstance) => {
          // Super property: stamped on every event (environment is tagged centrally by the backend proxy via POSTHOG_ENV)
          if (typeof phInstance?.register === 'function') {
            phInstance.register({
              app: TELEMETRY_APP_NAME,
            });
          }
          this.listenToRouteChanges();
          phInstance.capture('application_initialized');
        },
      });
    } catch (e) {
      console.warn('Failed to initialize PostHog telemetry:', e);
    }
  }

  /**
   * Replaces every URL-like value (by key name or by value) with its sanitized route
   */
  private sanitizeUrlProperties(props: Record<string, any> | undefined): void {
    if (!props) return;
    for (const key of Object.keys(props)) {
      const val = props[key];
      const lowerKey = key.toLowerCase();
      const isUrlKey =
        lowerKey.includes('url') ||
        lowerKey.includes('referrer') ||
        lowerKey.includes('href') ||
        lowerKey.includes('pathname');

      if (typeof val === 'string' && (isUrlKey || /^https?:\/\//i.test(val))) {
        props[key] = this.sanitizeRouteUrl(val);
      }
    }
  }

  /**
   * Tracks route changes for app flow and dwell time calculations
   */
  private listenToRouteChanges(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const sanitizedUrl = this.sanitizeRouteUrl(event.urlAfterRedirects);
        this.trackPage(sanitizedUrl, document.title);
      });
  }

  /**
   * Normalizes routes to prevent patient/agency IDs from leaking in URLs
   * e.g. /patients/65a3d7b... becomes /patients/:id
   * and /view-pdf/<file name, which can carry a patient's name> becomes /view-pdf/:file
   */
  public sanitizeRouteUrl(url: string): string {
    if (!url) return '/';
    const [path] = url.split('?')[0].split('#');
    // Replace PDF file names, emails, hex ObjectIds or UUIDs with :param
    return path
      .replace(/\/view-pdf\/[^/]+/gi, '/view-pdf/:file')
      .replace(/\/[^/]*@[^/]*/g, '/:email')
      .replace(/\/[0-9a-fA-F]{24}\b/g, '/:id')
      .replace(/\/[0-9a-fA-F-]{36}\b/g, '/:id');
  }

  /**
   * Record page navigation
   */
  public trackPage(url?: string, title?: string): void {
    try {
      posthog.capture('$pageview', {
        $current_url: url || window.location.pathname,
        page_title: title || document.title,
      });
    } catch (e) {
      // non-blocking
    }
  }

  /**
   * Captures custom product events (e.g. 'esoc_form_submitted', 'qa_scrub_started')
   */
  public captureEvent(eventName: string, properties: Record<string, any> = {}): void {
    try {
      posthog.capture(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      // non-blocking
    }
  }

  /**
   * Captures runtime errors, UI exceptions, and system diagnostics
   */
  public captureError(error: any, context: Record<string, any> = {}): void {
    try {
      const errorType =
        context?.error_type ||
        error?.name ||
        (typeof error === 'object' && error?.constructor && error.constructor.name !== 'Object'
          ? error.constructor.name
          : null) ||
        'ApplicationError';

      const route = typeof window !== 'undefined' ? this.sanitizeRouteUrl(window.location?.pathname) : '/';
      const safeContext = {
        ...context,
        error_type: errorType,
        route,
        timestamp: new Date().toISOString(),
      };

      // Security guardrail: Synthetic Error with errorType prevents raw messages from quoting PHI/patient data
      // while preserving stack traces and diagnostic classification (only the parsed frames are sent)
      const safeError = new Error(errorType);
      safeError.name = errorType;
      if (error?.stack) {
        safeError.stack = error.stack;
      }
      posthog.captureException(safeError, safeContext);
    } catch (e) {
      // non-blocking
    }
  }

  /**
   * Identifies user by opaque/non-sensitive hash or ID (Never plain email or full name)
   */
  public identifyUser(opaqueUserId: string, traits: Record<string, any> = {}): void {
    try {
      if (!opaqueUserId) return;
      posthog.identify(opaqueUserId, {
        ...traits,
        identified_at: new Date().toISOString(),
      });
    } catch (e) {
      // non-blocking
    }
  }

  /**
   * Resets user session on logout
   */
  public resetUser(): void {
    try {
      posthog.reset();
    } catch (e) {
      // non-blocking
    }
  }
}
